import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbContextType {
  breadcrumbs: BreadcrumbItem[];
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
  setChargerBreadcrumbs: (unitName: string) => void;
  setMachineBreadcrumbs: (unitName: string) => void;
  setProjectBreadcrumbs: (projectName: string) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

export const useBreadcrumbs = () => {
  const context = useContext(BreadcrumbContext);
  if (context === undefined) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return context;
};

// Custom hook to automatically update breadcrumbs based on route
export const useRouteBreadcrumbs = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    const pathname = location.pathname;
    const pathSegments = pathname.split('/').filter(Boolean);
    
    let newBreadcrumbs: BreadcrumbItem[] = [];
    
    // Show breadcrumbs for main pages and detail pages
    // Path structure: /organization/:orgId/map, /organization/:orgId/projects, etc.
    if (pathSegments.length === 3) {
      // Main navigation pages - show just the page name (3rd element)
      const pageName = pathSegments[2];
      if (pageName === 'map') {
        newBreadcrumbs = [{ label: 'Map' }];
      } else if (pageName === 'projects') {
        newBreadcrumbs = [{ label: 'My Projects' }];
      } else if (pageName === 'assets') {
        newBreadcrumbs = [{ label: 'Assets' }];
      } else if (pageName === 'integrations') {
        newBreadcrumbs = [{ label: 'Integrations' }];
      } else if (pageName === 'admin') {
        newBreadcrumbs = [{ label: 'Administration' }];
      }
    } else if (pathSegments.length === 4) {
      // Asset and Admin pages - show just the specific page name (4th element)
      if (pathSegments[2] === 'assets') {
        const assetType = pathSegments[3];
        let assetLabel = '';
        if (assetType === 'chargers') {
          assetLabel = 'Chargers';
        } else if (assetType === 'switchboards') {
          assetLabel = 'Switchboards';
        } else if (assetType === 'energy-meters') {
          assetLabel = 'Energy Meters';
        } else if (assetType === 'excavators') {
          assetLabel = 'Excavators';
        } else {
          assetLabel = assetType.charAt(0).toUpperCase() + assetType.slice(1).replace('-', ' ');
        }
        newBreadcrumbs = [{ label: assetLabel }];
      } else if (pathSegments[2] === 'integrations') {
        // Integration detail page
        if (pathSegments[3] === 'data') {
          newBreadcrumbs = [{ label: 'Data Integrations' }];
        }
      } else if (pathSegments[2] === 'admin') {
        // Admin detail page - show just the specific page name
        const adminType = pathSegments[3];
        let adminLabel = '';
        if (adminType === 'users') {
          adminLabel = 'Users';
        } else if (adminType === 'companies') {
          adminLabel = 'Companies';
        } else {
          adminLabel = adminType.charAt(0).toUpperCase() + adminType.slice(1);
        }
        newBreadcrumbs = [{ label: adminLabel }];
      }
    } else if (pathSegments.length === 5) {
      // Level 4 pages (5 path segments) - e.g., /organization/:orgId/assets/chargers/:unitId
      if (pathSegments[2] === 'assets' && pathSegments[3] === 'chargers' && params.unitId) {
        // Charger detail page - Let the component handle this with actual unit name
        // Don't set breadcrumbs here, let ChargerDetails component handle it
      } else if (pathSegments[2] === 'projects' && params.projectId) {
        // Project detail page - Let the component handle this with actual project name
        // Don't set breadcrumbs here, let ProjectDetails component handle it
      }
    }
    
    setBreadcrumbs(newBreadcrumbs);
  }, [location.pathname, params.projectId, params.unitId, setBreadcrumbs]);
};

interface BreadcrumbProviderProps {
  children: ReactNode;
}

export const BreadcrumbProvider: React.FC<BreadcrumbProviderProps> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  const setChargerBreadcrumbs = useCallback((unitName: string) => {
    setBreadcrumbs([
      { label: 'Chargers', path: '/assets/chargers' },
      { label: unitName }
    ]);
  }, []);

  const setMachineBreadcrumbs = useCallback((unitName: string) => {
    setBreadcrumbs([
      { label: 'Excavators', path: '/assets/excavators' },
      { label: unitName }
    ]);
  }, []);

  const setProjectBreadcrumbs = useCallback((projectName: string) => {
    setBreadcrumbs([
      { label: 'My Projects', path: '/projects' },
      { label: projectName }
    ]);
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ 
      breadcrumbs, 
      setBreadcrumbs,
      setChargerBreadcrumbs,
      setMachineBreadcrumbs,
      setProjectBreadcrumbs
    }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};
